import { Injectable, OnDestroy, Inject } from '@angular/core';
import StatisticDefinition from '@arcgis/core/rest/support/StatisticDefinition';

import {
  asyncScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  finalize,
  first,
  map,
  mergeMap,
  Observable,
  observeOn,
  of,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';
import {
  entityIdFieldName,
  typeObjectFieldName,
} from 'app/shared/constants/search-results.constant';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { LoadOptionsParams } from 'app/shared/helpers/query-params/load-options-params';
import { FilterOperatorType } from 'app/shared/models/filter-operator-type.model';
import { AuthService } from 'app/shared/services/auth.service';
import {
  CacheFactoryService,
  ICacheService,
} from 'app/shared/services/cache-factory.service';
import { EntityService } from 'app/shared/services/entity.service';
import {
  ExportService,
  LayersExportOptions,
} from 'app/shared/services/export.service';
import { MapDomainService } from 'app/shared/services/map-domain.service';
import { SiiaAttachmentsService } from 'app/shared/services/siia-attachments.service';
import { ToastService } from 'app/shared/services/toast.service';
import { ArrayUtils } from 'app/shared/utils/array.utils';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { FormatterUtils } from 'app/shared/utils/formatter.utils';
import { measurement } from '../../default-symbols';
import { HighlightService } from '../../highlight.service';
import { MainMapService } from '../../main-map.service';
import { MapFilterItem, MapFilterType } from './map-filter-state.model';
import {
  ACTIVE_FILTERS_ON_MAP_MANAGER,
  MapFilterManager,
} from './map-filter.manager';
import { SEARCH_LOCAL_LOADING } from './search-results.component';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import { AppConfigService } from '@services/app-config.service';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { TranslationService } from '@services/translation.service';

interface ValueWithOperator {
  operator: FilterOperatorType;
  value: any;
  type: MapFilterType;
}

interface FieldValueWithOperator extends ValueWithOperator {
  field: string;
}

export interface StatisticsDataItemLike {
  value: any;
  alias?: string;
  count: number;
}

@Injectable()
export class SearchResultsService implements OnDestroy {
  private cacheService: ICacheService;

  private mapView: __esri.MapView;
  private mainMapImageLayer: __esri.MapImageLayer;
  private filterItemList: MapFilterItem[];
  private layerList: __esri.Sublayer[];

  private _featureLayerMap = new Map<__esri.Sublayer, __esri.FeatureLayer>();

  private layersLoadedSubject = new BehaviorSubject<__esri.Sublayer[]>(null);

  get layers() {
    return this.layersLoadedSubject.getValue();
  }

  readonly onFieldsLoaded = this.layersLoadedSubject.asObservable().pipe(
    map((layers) => {
      if (!layers) {
        return null;
      }

      const fieldList: __esri.Field[] = [];
      const fieldsSet = new Set<string>();

      layers.forEach((layer) => {
        const fields = layer.fields;
        fields.forEach((field) => {
          if (!fieldsSet.has(field.name)) {
            fieldsSet.add(field.name);
            fieldList.push(field);
          }
        });
      });

      return fieldList;
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    })
  );

  readonly onExportFieldsLoaded = this.onFieldsLoaded.pipe(
    map((fieldList) => {
      return fieldList?.filter((field) => {
        return (
          !geomFieldsIgnored.includes(field.name) &&
          !systemFieldTypesIgnored.includes(field.type)
        );
      });
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );
  readonly onEstatisticsFieldsLoaded = this.onExportFieldsLoaded.pipe(
    map((fieldList) => {
      const statisticsIgnoreFields = [
        'objectid',
        'idobjeto',
        'cdt',
        'npolicia',
        'identidade',
        'identidadesiia',
        'identidade_siia',
        'idobjetocadastro',
      ];
      const statisticsFields = fieldList?.filter(
        (field) => !statisticsIgnoreFields.includes(field.name)
      );

      return [
        'distrito',
        'concelho',
        'freguesia',
        'arruamento',
        'tipoobjecto',
        'afetacaoprincipal',
        'nomeentidade',
        'tipoentidade',
        'largura',
        'altura',
        'comprimento',
        'diametro',
        'cota',
        'tracado',
        'tipoutilizacao',
        'estadooperacional',
        'estadoocupacao',
        'created_date',
        'last_edited_date',
        'baseinstalacao',
      ]
        .map((name) => {
          const field = statisticsFields?.find((x) => x.name === name);
          return field;
        })
        .filter((x) => !!x);
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  private geometryToSearchSubject = new BehaviorSubject<__esri.Geometry>(null);

  private prepareFilterSubject = new BehaviorSubject<boolean>(false);
  readonly onPreparationChanged = this.prepareFilterSubject.asObservable();

  private filterOnMapSubject = new ReplaySubject<void>(1);

  private localizationGeomSubject = new Subject<__esri.Geometry>();

  private clearInternalSubject = new Subject<void>();
  readonly onCleared = this.clearInternalSubject.asObservable();

  private setupSubscription: Subscription;

  constructor(
    private appConfig: AppConfigService,
    private authService: AuthService,
    private mainMapService: MainMapService,
    private mapFilterManager: MapFilterManager,
    @Inject(ACTIVE_FILTERS_ON_MAP_MANAGER)
    private activeFiltersOnMapManager: MapFilterManager,
    private siiaAttachmentsService: SiiaAttachmentsService,
    private mapDomainService: MapDomainService,
    private entityService: EntityService,
    private highlightService: HighlightService,
    private exportService: ExportService,
    @Inject(SEARCH_LOCAL_LOADING) public loadingController: LoadingController,
    private globalLoadingController: LoadingController,
    private toastService: ToastService,
    cacheFactoryService: CacheFactoryService,
    private ts: TranslationService
  ) {
    this.cacheService = cacheFactoryService.getOrCreate(
      'search-results-service'
    );
  }

  ngOnDestroy(): void {
    this.setupSubscription?.unsubscribe();
    this.cacheService?.dispose();

    EsriUtils.destroyLayersRecursive(
      Array.from(this._featureLayerMap.values())
    );
    this._featureLayerMap.clear();
  }

  setGeometry(geom: __esri.Geometry) {
    this.geometryToSearchSubject.next(geom);
  }

  clearLocalizationHighlight() {
    this.localizationGeomSubject.next(null);
  }

  clear() {
    this.clearInternalSubject.next();
    this.disableSearch();
  }

  setup(mapView: __esri.MapView, mainMapImageLayer: __esri.MapImageLayer) {
    this.setupSubscription?.unsubscribe();
    if (!mapView || !mainMapImageLayer) {
      return;
    }

    this.mapView = mapView;
    this.mainMapImageLayer = mainMapImageLayer;

    const subscription = new Subscription();

    const layerList =
      (
        EsriUtils.getDeeperLayers(mainMapImageLayer) as __esri.Sublayer[]
      )?.reverse() ?? [];

    this.layerList = layerList;

    const layerListObservable = EsriUtils.load(layerList).pipe(
      map(() => layerList),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    subscription.add(
      layerListObservable.subscribe((layers) => {
        this.layersLoadedSubject.next(layers);
      })
    );

    subscription.add(
      this.getFilterOnMapObservable(layerListObservable).subscribe()
    );

    subscription.add(
      this.localizationGeomSubject
        .pipe(
          switchMap((geometry) => {
            if (!geometry) {
              return EMPTY;
            }

            this.mainMapService.goTo(geometry);

            return this.highlightService.highlight(
              geometry,
              measurement.polygonSymbolDefault
            );
          })
        )
        .subscribe()
    );

    subscription.add(
      this.prepareFilterSubject.subscribe((active) => {
        if (active) {
          this.filterItemList = this.mapFilterManager.getFilters();
        } else {
          this.filterItemList = null;
        }
      })
    );

    subscription.add(
      this.clearInternalSubject.subscribe(() => {
        this.clearFilters();

        this.cacheService.clear();
      })
    );

    subscription.add(() => {
      this.layersLoadedSubject.next(null);

      this.mapView = null;
      this.mainMapImageLayer = null;
      this.filterItemList = null;
      this.layerList = null;

      this.cacheService.clear();
    });

    this.setupSubscription = subscription;
  }

  getAttachmentsFromRegisterElementCDT(graphic: __esri.Graphic) {
    const entityId = graphic.attributes[entityIdFieldName];
    const cdt = graphic.attributes['cdt'];

    const key = `entityId: ${entityId} | cdt: ${cdt}`;

    return this.cacheService.getCached({
      path: `getAttachmentsFromRegisterElementCDT@@${key}`,
      source: () => {
        return this.siiaAttachmentsService.getAttachmentsFromRegisterElementCDT(
          entityId,
          cdt,
          1
        );
      },
    });
  }

  getFeatureGeometry(graphic: __esri.Graphic): Observable<__esri.Geometry> {
    let typeObject = graphic.attributes[typeObjectFieldName];

    const objectIdFieldName = 'objectid';
    const objectId = graphic.attributes[objectIdFieldName];

    return this.cacheService.getCached({
      path: `${typeObject}@@${objectId}`,
      source: () => {
        return this.onFieldsLoaded.pipe(
          first(),
          map((fields) => {
            typeObject = typeObject?.toLowerCase();

            const field = fields?.find((x) => x.name === typeObjectFieldName);
            const codedValues = (field?.domain as __esri.CodedValueDomain)
              ?.codedValues;
            return (
              codedValues?.find((x) => {
                return String(x.code).toLowerCase() === typeObject;
              })?.name ?? typeObject
            );
          }),
          mergeMap((layerTitle) => {
            layerTitle = layerTitle?.toLowerCase();

            const layer = this.layerList?.find((layer) => {
              return layer.title.toLowerCase() === layerTitle;
            });

            if (!layer) {
              return of(null);
            }

            const query = layer.createQuery();
            query.where = `${objectIdFieldName}=${objectId}`;
            query.outFields = [objectIdFieldName];
            query.returnGeometry = true;
            query.num = 1;
            return EsriUtils.queryFeatures(layer, query).pipe(
              map((featureSet) => {
                return featureSet?.features?.[0]?.geometry;
              })
            );
          })
        );
      },
    });
  }

  observeCanExportSearchResults() {
    return this.observeSearchTotalCount().pipe(
      map((totalCount) => {
        return totalCount > 0;
      }),
      distinctUntilChanged(),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  observeCanExportShpSearchResults() {
    return this.authService.observeAuthInfo().pipe(
      map((x) => x?.entityId),
      switchMap((entityId) => {
        return this.observeSearchTotalCount(entityId);
      }),
      map((totalCount) => {
        return totalCount > 0;
      }),
      distinctUntilChanged(),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  shouldShowExportShpSearchResultsLimit() {
    return this.authService.observeAuthInfo().pipe(
      map((x) => x?.entityId),
      switchMap((entityId) => {
        return this.observeSearchTotalCount(entityId);
      }),
      map((totalCount) => {
        return totalCount > this.appConfig.exportLimit;
      }),
      take(1)
    );
  }

  exportSearchResults(shapeMode?: boolean) {
    return this.prepareFilterSubject.asObservable().pipe(
      switchMap((active) => {
        // if (!active) {
        //   return EMPTY;
        // }

        return combineLatest(
          this.onExportFieldsLoaded,
          this.geometryToSearchSubject,
          this.authService.getEntityId()
        ).pipe(
          switchMap(([fields, geometry, entityId]) => {
            let filterItemList = this.filterItemList;
            if (!fields?.length || (!filterItemList?.length && !geometry)) {
              return EMPTY;
            }

            filterItemList = filterItemList.filter(
              (x) => x.value !== MapFilterItem.DEFAULT_VALUE_ALL
            );

            const layerFilters = filterItemList?.filter(
              (x) => x.type === MapFilterType.ObjectType
            );

            let layerList = this.layerList;
            if (layerFilters?.length) {
              layerList = layerList.filter((layer) => {
                const layerAllowed = layerFilters.some((x) => {
                  return layer.id === x.value;
                });

                return layerAllowed;
              });
            }

            if (!layerList.length) {
              return EMPTY;
            }

            return this.getWhereClauseStrObservable(filterItemList).pipe(
              mergeMap((whereStr) => {
                if (shapeMode) {
                  whereStr = addEntityIdWhereClauseStr(entityId, whereStr);
                }

                const exportOptions: LayersExportOptions = {
                  fields,
                  layers: layerList.map((layer) => {
                    return layer.url;
                  }),
                  where: whereStr,
                  geometry,
                };

                if (shapeMode) {
                  return this.exportService.exportAsShpLink(exportOptions);
                }

                return this.exportService.exportAsXlsxLink(exportOptions);
              })
            );
          })
        );
      }),
      take(1),
      takeUntil(this.prepareFilterSubject.pipe(first((x) => !x)))
    );
  }

  searchOnMap() {
    this.filterOnMapSubject.next();
    this.activeFiltersOnMapManager.loadState(this.mapFilterManager.getState());
  }

  enableSearch() {
    this.cacheService.clear();
    this.prepareFilterSubject.next(true);
  }

  disableSearch() {
    this.prepareFilterSubject.next(false);
  }

  observeSearchTotalCount(entityId?: string): Observable<number> {
    return this.prepareFilterSubject.asObservable().pipe(
      observeOn(asyncScheduler),
      switchMap((active) => {
        // if (!active) {
        //   return of(0);
        // }

        return this.cacheService.getCached({
          path: `observeSearchTotalCount@@${entityId}`,
          lifetimeSeconds: 0,
          source: () => {
            this.loadingController.show();
            return this.getTotalCount(entityId).pipe(
              finalize(() => {
                this.loadingController.hide();
              })
            );
          },
        });
      })
    );
  }

  observeSearchFeatures(): Observable<__esri.Graphic[]> {
    return this.prepareFilterSubject.asObservable().pipe(
      observeOn(asyncScheduler),
      switchMap((active) => {
        // if (!active) {
        //   return of(null);
        // }

        return this.cacheService.getCached({
          path: 'observeSearchFeatures',
          lifetimeSeconds: 0,
          source: () => {
            this.loadingController.show();
            return this.searchFeatures().pipe(
              finalize(() => {
                this.loadingController.hide();
              })
            );
          },
        });
      })
    );
  }

  observeSearchFeaturesAttr(
    fieldName: string
  ): Observable<StatisticsDataItemLike[]> {
    return this.prepareFilterSubject.asObservable().pipe(
      observeOn(asyncScheduler),
      switchMap((active) => {
        if (!active || !fieldName) {
          return of(null);
        }

        this.loadingController.show();
        return this.cacheService
          .getCached({
            path: `searchAllAttributes@@${fieldName}`,
            lifetimeSeconds: 0,
            source: () => {
              return this.searchFeaturesGroupedCountByField(fieldName);
            },
          })
          .pipe(
            finalize(() => {
              this.loadingController.hide();
            })
          );
      })
    );
  }

  private clearFilters() {
    this.mapFilterManager.removeAllFilters();
    this.activeFiltersOnMapManager.removeAllFilters();
  }

  private searchFeatures(): Observable<__esri.Graphic[]> {
    return combineLatest(
      this.geometryToSearchSubject.pipe(first()),
      of(this.filterItemList)
    ).pipe(
      mergeMap(([geometry, filterItemList]) => {
        if (!(geometry || filterItemList?.length)) {
          return of([]);
        }

        filterItemList = filterItemList.filter(
          (x) => x.value !== MapFilterItem.DEFAULT_VALUE_ALL
        );

        const layerFilters = filterItemList.filter(
          (x) => x.type === MapFilterType.ObjectType
        );

        return this.getWhereClauseStrObservable(filterItemList).pipe(
          mergeMap((whereStr) => {
            // let layerHolderList = this.layerList.filter((layer) => {
            //   return layer.visible;
            // });

            let layerHolderList = this.layerList;

            if (layerFilters?.length) {
              layerHolderList = layerHolderList.filter((layer) => {
                const layerAllowed = layerFilters.some((x) => {
                  return layer.id === x.value;
                });

                return layerAllowed;
              });
            }

            const recursiveRequestDataFn = (
              index: number,
              limit: number
            ): Observable<
              {
                layerId: number;
                layerName: string;
                features: __esri.Graphic[];
              }[]
            > => {
              if (index > layerHolderList.length - 1) {
                return of(null);
              }

              const layer = layerHolderList[index];

              const query = layer.createQuery();
              query.outFields = ['*'];
              query.where = whereStr;
              query.geometry = geometry;
              query.returnGeometry = false;
              query.num = limit;

              return EsriUtils.queryFeatures(layer, query).pipe(
                map((featureSet) => {
                  const layerId = layer.id;
                  const layerName = layer.title;
                  const { features } = featureSet;

                  return {
                    layerId,
                    layerName,
                    features,
                  };
                }),
                mergeMap((res) => {
                  if (res?.features && res.features.length < limit) {
                    return recursiveRequestDataFn(
                      index + 1,
                      limit - res.features.length
                    ).pipe(
                      map((resList) => {
                        return [res].concat(resList);
                      })
                    );
                  } else {
                    return of([res]);
                  }
                })
              );
            };

            return recursiveRequestDataFn(0, 2000).pipe(
              map((res) => {
                const itemList = res?.flatMap((x) => x?.features ?? []);
                return itemList ?? [];
              })
            );
          }),
          mergeMap((res) => {
            return this.zoomToLocationByFilters(filterItemList).pipe(
              map(() => res)
            );
          }),
          catchError(() => {
            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-load-data']
            );

            return EMPTY;
          }),
          tap((itemList) => {
            if (!itemList.length) {
              this.toastService.warning(
                this.ts.getCurrentLanguage()['no-features-found']
              );
            }
          })
        );
      }),
      takeUntil(this.clearInternalSubject)
    );
  }

  private searchFeaturesGroupedCountByField(
    fieldName: string
  ): Observable<StatisticsDataItemLike[]> {
    return combineLatest(
      this.geometryToSearchSubject.pipe(first()),
      of(this.filterItemList)
    ).pipe(
      mergeMap(([geometry, filterItemList]) => {
        if (!(geometry || filterItemList?.length)) {
          return of(null);
        }

        filterItemList = filterItemList.filter(
          (x) => x.value !== MapFilterItem.DEFAULT_VALUE_ALL
        );

        const layerFilters = filterItemList.filter(
          (x) => x.type === MapFilterType.ObjectType
        );

        let layerList = this.layerList;
        if (layerFilters?.length) {
          layerList = layerList.filter((layer) => {
            const layerAllowed = layerFilters.some((x) => {
              return layer.id === x.value;
            });

            return layerAllowed;
          });
        }

        return this.getWhereClauseStrObservable(filterItemList).pipe(
          mergeMap((whereStr) => {
            const resultsObservable = combineLatest(
              layerList.map((layer) => {
                const query = layer.createQuery();
                query.outFields = [fieldName];
                query.geometry = geometry;
                query.where = whereStr;
                query.returnGeometry = false;
                query.returnDistinctValues = true;

                query.groupByFieldsForStatistics = [fieldName];
                query.outStatistics = [
                  new StatisticDefinition({
                    statisticType: 'count',
                    onStatisticField: layer.objectIdField,
                    outStatisticFieldName: 'count',
                  }),
                ];

                return EsriUtils.queryFeatures(layer, query).pipe(
                  map((featureSet) => {
                    return (
                      featureSet?.features?.map<StatisticsDataItemLike>((x) => {
                        const value = x.attributes[fieldName];
                        const count = x.attributes['count'];
                        return { value, count };
                      }) ?? []
                    );
                  })
                );
              })
            ).pipe(
              map((res) => {
                const groupMap = ArrayUtils.groupBy(
                  res.flat(),
                  (x) => x?.value
                );
                return Array.from(groupMap, ([value, items]) => {
                  const count = items.reduce((acc, v) => {
                    return acc + v.count;
                  }, 0);

                  return {
                    value,
                    count,
                  } as StatisticsDataItemLike;
                });
              })
            );

            return combineLatest(
              this.onFieldsLoaded.pipe(
                first(),
                map((fields) => {
                  const field = fields?.find((x) => x.name === fieldName);
                  return field;
                })
              ),
              resultsObservable
            );
          }),
          map(([field, results]) => {
            let formatterFn: (value: any) => any;

            if (field?.type === 'date') {
              formatterFn = (x) => FormatterUtils.formatDateTime(x);
            } else {
              const domain = field?.domain as __esri.CodedValueDomain;
              if (domain?.codedValues?.length) {
                formatterFn = (value) => {
                  const domainItem = domain.codedValues.find(
                    (x) => x.code === value
                  );
                  return domainItem?.name ?? value;
                };
              }
            }

            if (!formatterFn) {
              formatterFn = String;
            }

            results.forEach((item) => {
              item.alias = formatterFn(item.value);
            });

            return results;
          }),
          catchError(() => {
            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-load-data']
            );

            return EMPTY;
          })
        );
      }),
      takeUntil(this.clearInternalSubject)
    );
  }

  private getTotalCount(entityId?: string): Observable<number> {
    return combineLatest(
      this.geometryToSearchSubject.pipe(first()),
      of(this.filterItemList)
    ).pipe(
      mergeMap(([geometry, filterItemList]) => {
        if (!(geometry || filterItemList?.length)) {
          return of(0);
        }

        filterItemList = filterItemList.filter(
          (x) => x.value !== MapFilterItem.DEFAULT_VALUE_ALL
        );

        const layerFilters = filterItemList.filter(
          (x) => x.type === MapFilterType.ObjectType
        );
        return this.getWhereClauseStrObservable(filterItemList).pipe(
          mergeMap((whereStr) => {
            whereStr = addEntityIdWhereClauseStr(entityId, whereStr);

            return combineLatest(
              this.layerList
                .map((layer) => {
                  // if (!layer.visible) {
                  //   return null;
                  // }

                  if (layerFilters?.length) {
                    const layerAllowed = layerFilters.some((x) => {
                      return layer.id === x.value;
                    });
                    if (!layerAllowed) {
                      return null;
                    }
                  }

                  let featureLayer = this._featureLayerMap.get(layer);
                  if (!featureLayer) {
                    featureLayer = new FeatureLayer({
                      url: layer.url,
                      layerId: layer.id,
                    });
                    this._featureLayerMap.set(layer, featureLayer);
                  }

                  const query = featureLayer.createQuery();
                  query.where = whereStr;
                  query.geometry = geometry;

                  return EsriUtils.queryFeatureCount(featureLayer, query);
                })
                .filter((x) => !!x)
            );
          }),
          map((res) => {
            const totalCount =
              res?.reduce((acc, v) => {
                return acc + v;
              }, 0) ?? 0;

            return totalCount;
          }),
          catchError(() => {
            this.toastService.error(
              this.ts.getCurrentLanguage()['failed-load-data']
            );

            return EMPTY;
          })
        );
      }),
      takeUntil(this.clearInternalSubject)
    );
  }

  private getFilterOnMapObservable(
    layerListObservable: Observable<__esri.Sublayer[]>
  ) {
    return this.filterOnMapSubject.pipe(
      first(),
      observeOn(asyncScheduler),
      switchMap(() => {
        this.globalLoadingController.show();
        return layerListObservable.pipe(
          finalize(() => {
            this.globalLoadingController.hide();
          }),
          combineLatestWith(
            this.filterOnMapSubject.pipe(
              observeOn(asyncScheduler),
              startWith(null)
            )
          ),
          mergeMap(([layerList]) => {
            const filtersObservable =
              this.activeFiltersOnMapManager.onFiltersChanged.pipe(
                map((list) => {
                  return list.filter(
                    (x) => x.value !== MapFilterItem.DEFAULT_VALUE_ALL
                  );
                })
              );

            const zoomToLocationByFiltersObservable = filtersObservable.pipe(
              map((list) => {
                return list.filter(
                  (x) => x.type === MapFilterType.Localization
                );
              }),
              mapFiltersOpFn(),
              switchMap((list) => {
                this.globalLoadingController.show();
                return this.zoomToLocationByFilters(list).pipe(
                  finalize(() => {
                    this.globalLoadingController.hide();
                  })
                );
              }),
              map(() => null),
              distinctUntilChanged()
            );

            const layerFiltersObservable = filtersObservable.pipe(
              map((list) => {
                return list.filter((x) => x.type === MapFilterType.ObjectType);
              }),
              mapFiltersOpFn()
            );

            const whereStrObservable = filtersObservable.pipe(
              switchMap((list) => {
                return this.getWhereClauseStrObservable(list);
              }),
              distinctUntilChanged()
            );

            return combineLatest([
              this.geometryToSearchSubject,
              filtersObservable,
              layerFiltersObservable,
              zoomToLocationByFiltersObservable,
            ]).pipe(
              finalize(() => {
                for (const featureLayer of layerList) {
                  enableLayer(featureLayer);
                }
              }),
              debounceTime(0),
              switchMap(([geometry, filterItemList, layerFilters]) => {
                let layerIds = layerList.map((x) => x.id);
                if (layerFilters?.length) {
                  layerIds = layerIds.filter((layerId) => {
                    return layerFilters.some((x) => {
                      return layerId === x.value;
                    });
                  });
                }

                const idsStrByLayerId = new Map<number, number[]>();

                if (!geometry || !layerIds.length) {
                  return of({
                    idsStrByLayerId,
                    filterItemList,
                    layerIds,
                  });
                }

                const parameters = new IdentifyParameters({
                  geometry,
                  tolerance: 0,
                  width: this.mapView.width,
                  height: this.mapView.height,
                  layerOption: 'visible',
                  layerIds,
                  mapExtent: this.mapView.extent,
                  spatialReference: this.mapView.spatialReference,
                  returnGeometry: false,
                  returnFieldName: false,
                  returnUnformattedValues: true,
                });

                return EsriUtils.identify(
                  this.mainMapImageLayer.url,
                  parameters
                ).pipe(
                  map((res) => {
                    res?.results?.forEach((x) => {
                      let list = idsStrByLayerId.get(x.layerId);
                      if (!list) {
                        list = [];
                        idsStrByLayerId.set(x.layerId, list);
                      }

                      const id = x.feature.attributes.objectid;
                      list.push(id);
                    });

                    layerIds = layerIds.filter((id) => {
                      return idsStrByLayerId.has(id);
                    });

                    return {
                      idsStrByLayerId,
                      filterItemList,
                      layerIds,
                    };
                  })
                );
              }),
              combineLatestWith(whereStrObservable),
              switchMap(
                ([{ idsStrByLayerId, filterItemList, layerIds }, whereStr]) => {
                  if (!(idsStrByLayerId?.size || filterItemList?.length)) {
                    for (const featureLayer of layerList) {
                      enableLayer(featureLayer);
                    }

                    return EMPTY;
                  }

                  return new Observable((subscriber) => {
                    for (const featureLayer of layerList) {
                      if (layerIds?.length) {
                        const layerAllowed = layerIds.includes(featureLayer.id);
                        if (!layerAllowed) {
                          disableLayer(featureLayer);
                          continue;
                        }
                      }

                      subscriber.add(
                        EsriUtils.isLayerVisibleObservable(
                          featureLayer
                        ).subscribe((visible) => {
                          if (visible) {
                            const idsStr = idsStrByLayerId
                              ?.get?.(featureLayer.id)
                              ?.join?.(',');
                            setFiltersOnLayer(featureLayer, whereStr, idsStr);
                          } else {
                            disableLayer(featureLayer);
                          }
                        })
                      );
                    }
                  });
                }
              ),
              takeUntil(this.clearInternalSubject.pipe(first()))
            );
          })
        );
      })
    );
  }

  private zoomToLocationByFilters(
    filterItemList: MapFilterItem[]
  ): Observable<any> {
    return this.getGeometryByLocalizationFilters(filterItemList).pipe(
      tap((geometry) => {
        this.localizationGeomSubject.next(geometry);
      })
    );
  }

  private getGeometryByLocalizationFilters(filterItemList: MapFilterItem[]) {
    filterItemList = filterItemList?.filter(
      (x) => x.type === MapFilterType.Localization
    );

    const district = filterItemList?.find((x) => x.field === 'distrito')?.value;
    const county = filterItemList?.find((x) => x.field === 'concelho')?.value;
    const parish = filterItemList?.find((x) => x.field === 'freguesia')?.value;

    if (parish) {
      return this.mapDomainService.getParishGeometryByLabel(
        district,
        county,
        parish
      );
    }

    if (county) {
      return this.mapDomainService.getCountyGeometryByLabel(district, county);
    }

    if (district) {
      return this.mapDomainService.getDistrictGeometryByLabel(district);
    }

    return of(null);
  }

  private getWhereClauseStrObservable(
    filterItemList: MapFilterItem[]
  ): Observable<string> {
    return defer(() => {
      if (!filterItemList?.length) {
        return of('1=1');
      }

      const customTypes = [MapFilterType.EntityType, MapFilterType.ObjectType];

      const filters = filterItemList.filter(
        (x) => !customTypes.includes(x.type)
      );

      const loadOptions = new LoadOptionsParams();

      this.addFiltersToLoadOptions(
        loadOptions,
        filters.map<FieldValueWithOperator>(
          ({ fieldHandled, operator, value, type }) => {
            return { field: fieldHandled, operator, value, type };
          }
        )
      );

      const entityTypeFilters = filterItemList.filter(
        (x) => x.type === MapFilterType.EntityType
      );

      let observable: Observable<any>;

      if (entityTypeFilters.length) {
        observable = combineLatest(
          entityTypeFilters.map((x) =>
            this.entityService.listEntitiesByEntityType(x.valueAsStr).pipe(
              map((list) => {
                const { operator, type } = x;
                return { field: entityIdFieldName, operator, type, list };
              })
            )
          )
        ).pipe(
          tap((res) => {
            this.addFiltersToLoadOptions(
              loadOptions,
              res.flatMap(({ list, operator, field, type }) => {
                return list.map<FieldValueWithOperator>((x) => {
                  return { field, operator, value: x.id, type };
                });
              })
            );
          })
        );
      } else {
        observable = of(null);
      }

      return observable.pipe(
        map(() => {
          return loadOptions.toWhereClauseStr();
        })
      );
    });
  }

  private addFiltersToLoadOptions(
    loadOptions: LoadOptionsParams,
    list: FieldValueWithOperator[]
  ) {
    const filtersGrouped = this.toFieldValueGrouped(list);
    for (const [type, group] of filtersGrouped) {
      for (const [field, items] of group) {
        this.addGroupWhereClauseToLoadOptions(loadOptions, field, items);
      }
    }
  }

  private toFieldValueGrouped(list: FieldValueWithOperator[]) {
    return list.reduce((acc, item) => {
      let map = acc.get(item.type);
      if (!map) {
        map = new Map();
        acc.set(item.type, map);
      }

      let list = map.get(item.field);
      if (!list) {
        list = [];
        map.set(item.field, list);
      }

      list.push(item);
      return acc;
    }, new Map<MapFilterType, Map<string, ValueWithOperator[]>>());
  }

  private addGroupWhereClauseToLoadOptions(
    loadOptions: LoadOptionsParams,
    field: string,
    itemOrList: ValueWithOperator | ValueWithOperator[]
  ) {
    if (!Array.isArray(itemOrList)) {
      const { operator, value } = itemOrList;
      loadOptions.appendFilter([field, operator, value]);
    } else {
      const listWithEqOperator = itemOrList.filter(
        (x) => x.operator === FilterOperatorType.Eq
      );

      if (listWithEqOperator.length) {
        loadOptions.appendFilter([
          field,
          'in',
          listWithEqOperator.map((x) => x.value),
        ]);
      }

      const listWithoutEqOperator = itemOrList.filter(
        (x) => x.operator !== FilterOperatorType.Eq
      );
      if (listWithoutEqOperator.length) {
        listWithoutEqOperator.forEach((item) => {
          this.addGroupWhereClauseToLoadOptions(loadOptions, field, item);
        });
      }
    }
  }
}

function addEntityIdWhereClauseStr(entityId: string, whereStr: string) {
  if (entityId) {
    whereStr = `${entityIdFieldName}='${entityId}' and (${whereStr})`;
  }

  return whereStr;
}

function mapFiltersOpFn() {
  return (source: Observable<MapFilterItem[]>) => {
    return source.pipe(
      map((list) => {
        return list.reduce((acc, x) => {
          acc.set(x.value, x);
          return acc;
        }, new Map<any, MapFilterItem>());
      }),
      distinctUntilChanged((prev, curr) => {
        if (prev.size !== curr.size) {
          return false;
        }

        for (const [key, item] of prev) {
          if (curr.get(key) !== item) {
            return false;
          }
        }

        return true;
      }),
      map((res) => {
        return Array.from(res.values());
      })
    );
  };
}

function setFiltersOnLayer(
  featureLayer: __esri.Sublayer,
  whereStr: string,
  idsStr?: string
) {
  featureLayer.definitionExpression =
    (whereStr || '1=1') + (!idsStr ? '' : ` AND (OBJECTID in (${idsStr}))`);
}

function enableLayer(featureLayer: __esri.Sublayer) {
  setFiltersOnLayer(featureLayer, null);
}

function disableLayer(featureLayer: __esri.Sublayer) {
  setFiltersOnLayer(featureLayer, '1=0');
}
